import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from './Toast';

function fmtDate(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.8px]">{label}</p>
      <p className="text-[12px] text-[#111827] mt-0.5 leading-snug break-words">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  const items = Array.isArray(children) ? children.flat() : [children];
  if (!items.some(Boolean)) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-[0.9px] mb-2">{title}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

function emergencyContact(p) {
  const name  = p.travellerType === 'Player' ? p.guardianName  : p.emergencyContactName;
  const phone = p.travellerType === 'Player' ? p.guardianPhone : p.emergencyContactPhone;
  if (!name && !phone) return null;
  return [name, phone].filter(Boolean).join(' · ');
}

const NO_RISK_VALUES = new Set(['none', 'n/a', 'na', 'no', 'nil', 'n', '-', 'not applicable', 'not applicable.', 'no allergies', 'no dietary requirements', 'none.', 'no.', 'n/a.']);
const hasRisk = (v) => !!v && !NO_RISK_VALUES.has(v.trim().toLowerCase());
const isHighRisk = (p) => hasRisk(p.allergies) || hasRisk(p.dietaryRequirements) || hasRisk(p.safetyInfo);

function csvEscape(val) {
  if (val == null || val === '') return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
}

function buildCsv(rows) {
  const hasMicCup = rows.some((p) => p.formVariant === 'mic_cup');
  const hasLlsCentre = rows.some((p) => p.llsCentre);
  const headers = [
    'Traveller Type', ...(hasLlsCentre ? ['LLS Centre'] : []), 'First Name', 'Middle Names', 'Surname', 'Gender', 'Date of Birth',
    'Contact Phone', 'Contact Email',
    'Guardian Name', 'Guardian Phone', 'Guardian Email',
    'Emergency Contact Name', 'Emergency Contact Phone',
    'Allergies', 'Dietary Requirements', 'Medical Conditions', 'Safety Info',
    'Swimming Level',
    'Passport Number', 'Passport Expiry', 'Nationality', 'Country of Issue',
    ...(hasMicCup ? [
      'Guardian Passport Number', 'Guardian Nationality',
      'Shirt Number', 'Position',
      'Flight Arrangement', 'Travel Group Size',
      'Outbound Flight Details', 'Outbound Flight Number',
      'Return Flight Details', 'Return Flight Number',
      'Insurance Confirmed',
    ] : []),
    'High Risk',
  ];
  const lines = [headers.join(',')];
  for (const p of rows) {
    lines.push([
      p.travellerType, ...(hasLlsCentre ? [p.llsCentre] : []), p.firstName, p.middleNames, p.surname, p.gender, p.dateOfBirth,
      p.contactPhone, p.contactEmail,
      p.guardianName, p.guardianPhone, p.guardianEmail,
      p.emergencyContactName, p.emergencyContactPhone,
      p.allergies, p.dietaryRequirements, p.conditions, p.safetyInfo,
      p.swimmingLevel,
      p.passportNumber, fmtDate(p.passportExpiry), p.nationality, p.countryOfIssue,
      ...(hasMicCup ? [
        p.guardianPassportNumber, p.guardianNationality,
        p.shirtNumber, p.position,
        p.flightArrangementMethod, p.travelGroupSize,
        p.outboundFlightDetails, p.outboundFlightNumber,
        p.returnFlightDetails, p.returnFlightNumber,
        p.formVariant === 'mic_cup' ? (p.insuranceConfirmed ? 'Yes' : 'No') : '',
      ] : []),
      isHighRisk(p) ? 'Yes' : 'No',
    ].map(csvEscape).join(','));
  }
  return lines.join('\r\n');
}

function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const TRAVELLER_TYPES = ['Player', 'Coach', 'Teacher', 'Parent/Guardian', 'Staff', 'Other'];
const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];

function EditField({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
      />
    </div>
  );
}

function EditTextArea({ label, value, onChange }) {
  return (
    <div className="col-span-2">
      <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">{label}</label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
      />
    </div>
  );
}

function ParticipantEditModal({ requestId, participant, onClose, onSaved }) {
  const [form, setForm] = useState({ ...participant });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  function set(field, val) { setForm((p) => ({ ...p, [field]: val })); }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const { id, ...rest } = form;
      await updateDoc(doc(db, 'requests', requestId, 'participants', participant.id), {
        ...rest,
        updatedAt: serverTimestamp(),
      });
      onSaved({ ...form });
      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(13,34,64,0.45)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-[14px] w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F1F3F6] flex-shrink-0">
          <h3 className="text-[15px] font-bold text-[#111827]">
            Edit Participant — {[form.firstName, form.surname].filter(Boolean).join(' ') || 'Unknown'}
          </h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151] text-xl w-7 h-7 flex items-center justify-center">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Personal */}
          <div>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Personal</p>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="First name"    value={form.firstName}    onChange={(v) => set('firstName', v)} />
              <EditField label="Middle names"  value={form.middleNames}  onChange={(v) => set('middleNames', v)} />
              <EditField label="Surname"       value={form.surname}      onChange={(v) => set('surname', v)} />
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Traveller type</label>
                <select value={form.travellerType || ''} onChange={(e) => set('travellerType', e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                  <option value="">—</option>
                  {TRAVELLER_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Gender</label>
                <select value={form.gender || ''} onChange={(e) => set('gender', e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                  <option value="">—</option>
                  {GENDERS.map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <EditField label="Date of birth" value={form.dateOfBirth} onChange={(v) => set('dateOfBirth', v)} type="date" />
              <EditField label="Phone"         value={form.contactPhone} onChange={(v) => set('contactPhone', v)} type="tel" />
              <EditField label="Email"         value={form.contactEmail} onChange={(v) => set('contactEmail', v)} type="email" />
            </div>
          </div>

          {/* Guardian */}
          <div>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Guardian / Parent</p>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Name"  value={form.guardianName}  onChange={(v) => set('guardianName', v)} />
              <EditField label="Phone" value={form.guardianPhone} onChange={(v) => set('guardianPhone', v)} type="tel" />
              <EditField label="Email" value={form.guardianEmail} onChange={(v) => set('guardianEmail', v)} type="email" />
              {form.formVariant === 'mic_cup' && (
                <>
                  <EditField label="Guardian passport number" value={form.guardianPassportNumber} onChange={(v) => set('guardianPassportNumber', v)} />
                  <EditField label="Guardian nationality"      value={form.guardianNationality}    onChange={(v) => set('guardianNationality', v)} />
                </>
              )}
            </div>
          </div>

          {/* Emergency Contact (non-Player travellers only) */}
          {form.travellerType !== 'Player' && (
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Emergency Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Name"  value={form.emergencyContactName}  onChange={(v) => set('emergencyContactName', v)} />
                <EditField label="Phone" value={form.emergencyContactPhone} onChange={(v) => set('emergencyContactPhone', v)} type="tel" />
              </div>
            </div>
          )}

          {/* MIC Cup — Playing Details */}
          {form.formVariant === 'mic_cup' && (
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Playing Details</p>
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Shirt number" value={form.shirtNumber} onChange={(v) => set('shirtNumber', v)} />
                <EditField label="Position"     value={form.position}    onChange={(v) => set('position', v)} />
              </div>
            </div>
          )}

          {/* MIC Cup — Travel Arrangements */}
          {form.formVariant === 'mic_cup' && (
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Travel Arrangements</p>
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Flight arrangement" value={form.flightArrangementMethod} onChange={(v) => set('flightArrangementMethod', v)} />
                <EditField label="Travel group size"  value={form.travelGroupSize}         onChange={(v) => set('travelGroupSize', v)} />
                <EditTextArea label="Outbound flight details" value={form.outboundFlightDetails} onChange={(v) => set('outboundFlightDetails', v)} />
                <EditField    label="Outbound flight number"  value={form.outboundFlightNumber}  onChange={(v) => set('outboundFlightNumber', v)} />
                <EditTextArea label="Return flight details"   value={form.returnFlightDetails}   onChange={(v) => set('returnFlightDetails', v)} />
                <EditField    label="Return flight number"    value={form.returnFlightNumber}    onChange={(v) => set('returnFlightNumber', v)} />
              </div>
            </div>
          )}

          {/* Medical */}
          <div>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Medical / Dietary</p>
            <div className="grid grid-cols-2 gap-3">
              <EditTextArea label="Allergies"          value={form.allergies}            onChange={(v) => set('allergies', v)} />
              <EditTextArea label="Dietary requirements" value={form.dietaryRequirements} onChange={(v) => set('dietaryRequirements', v)} />
              <EditTextArea label="Medical conditions" value={form.conditions}           onChange={(v) => set('conditions', v)} />
              <EditTextArea label="Safety info"        value={form.safetyInfo}           onChange={(v) => set('safetyInfo', v)} />
              <EditField    label="Swimming level"     value={form.swimmingLevel}        onChange={(v) => set('swimmingLevel', v)} />
            </div>
          </div>

          {/* Passport */}
          <div>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">Passport / Travel</p>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Passport number"  value={form.passportNumber} onChange={(v) => set('passportNumber', v)} />
              <EditField label="Expiry"           value={form.passportExpiry} onChange={(v) => set('passportExpiry', v)} type="date" />
              <EditField label="Nationality"      value={form.nationality}    onChange={(v) => set('nationality', v)} />
              <EditField label="Country of issue" value={form.countryOfIssue} onChange={(v) => set('countryOfIssue', v)} />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#F1F3F6] flex-shrink-0">
          {error && <p className="text-[12px] text-dr mb-2">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2 text-[13px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:bg-[#F9FAFB] transition">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ParticipantCards({ requestId, expectedCount, editable = false }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [expandedId, setExpandedId]     = useState(null);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [deletingId, setDeletingId]     = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const toast = useToast();

  async function handleDelete(id) {
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'requests', requestId, 'participants', id));
      setExpandedId((prev) => (prev === id ? null : prev));
      toast?.('Participant removed.', 'info');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', requestId, 'participants')),
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          const aRisk = isHighRisk(a) ? 0 : 1;
          const bRisk = isHighRisk(b) ? 0 : 1;
          if (aRisk !== bRisk) return aRisk - bRisk;
          const ta = a.createdAt?.toMillis?.() ?? a.submittedAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? b.submittedAt?.toMillis?.() ?? 0;
          return ta - tb;
        });
        setParticipants(docs);
        setLoading(false);
      }
    );
    return unsub;
  }, [requestId]);

  const registered  = participants.length;
  const total       = Number(expectedCount) || 0;
  const progress    = total > 0 ? Math.min(Math.round((registered / total) * 100), 100) : 0;
  const highRiskCount = participants.filter(isHighRisk).length;

  function fullName(p) {
    return [p.firstName, p.middleNames, p.surname].filter(Boolean).join(' ') || '—';
  }

  function handleExport(mode) {
    const rows = mode === 'high_risk' ? participants.filter(isHighRisk) : participants;
    const suffix = mode === 'high_risk' ? 'high-risk' : 'all';
    downloadCsv(buildCsv(rows), `participants-${suffix}.csv`);
  }

  return (
    <>
    {editingParticipant && (
      <ParticipantEditModal
        requestId={requestId}
        participant={editingParticipant}
        onClose={() => setEditingParticipant(null)}
        onSaved={(updated) => {
          setParticipants((prev) => prev.map((p) => p.id === updated.id ? updated : p));
          setEditingParticipant(null);
        }}
      />
    )}
    <ConfirmDialog
      open={!!confirmDeleteId}
      title="Remove participant?"
      message="This will permanently delete this participant's details and remove them from the tour and registers."
      confirmLabel="Remove"
      danger
      onConfirm={() => handleDelete(confirmDeleteId)}
      onCancel={() => setConfirmDeleteId(null)}
    />
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Participants</h3>
        <span className="text-[12px] text-[#9CA3AF]">
          {registered}{total > 0 ? ` / ${total}` : ''} registered
        </span>
      </div>

      {total > 0 && (
        <div className="w-full bg-[#E5E7EB] rounded-full h-1.5 mb-4">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${progress === 100 ? 'bg-sg' : 'bg-pb'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Export buttons */}
      {registered > 0 && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => handleExport('all')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[7px] hover:bg-blue-light transition"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export all
          </button>
          {highRiskCount > 0 && (
            <button
              onClick={() => handleExport('high_risk')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-[#92400E] border border-ba/40 rounded-[7px] bg-amber-light hover:bg-amber-light/80 transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export high risk ({highRiskCount})
            </button>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : participants.length === 0 ? (
        <p className="text-[13px] text-[#9CA3AF]">No participants registered yet.</p>
      ) : (
        <div className="space-y-2">
          {participants.map((p, i) => {
            const isExpanded = expandedId === p.id;
            const risk = isHighRisk(p);
            return (
              <div key={p.id} className={`border rounded-[10px] overflow-hidden ${risk ? 'border-ba/40' : 'border-[#E5E7EB]'}`}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : p.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition ${risk ? 'hover:bg-amber-light/50 bg-amber-light/20' : 'hover:bg-[#F9FAFB]'}`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-semibold text-[#111827] truncate">{i + 1}. {fullName(p)}</p>
                        {risk && (
                          <span className="inline-flex items-center gap-1 px-[7px] py-[2px] rounded-full text-[10px] font-bold bg-[#FEF3C7] text-[#92400E] border border-ba/30 flex-shrink-0">
                            <span className="w-[5px] h-[5px] rounded-full bg-[#D97706] flex-shrink-0" />
                            High risk
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-[#6B7280] mt-0.5">
                        {[
                          p.travellerType,
                          p.llsCentre ? `Centre: ${p.llsCentre}` : null,
                          p.dateOfBirth ? `DOB: ${fmtDate(p.dateOfBirth)}` : null,
                          emergencyContact(p) ? `Emergency: ${emergencyContact(p)}` : null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-[#9CA3AF] flex-shrink-0 ml-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-3 border-t border-[#F3F4F6] space-y-4">
                    {editable && (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingParticipant(p); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[8px] hover:bg-pb/5 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit participant
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id); }}
                          disabled={deletingId === p.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-dr border border-dr/30 rounded-[8px] hover:bg-dr/5 transition disabled:opacity-50"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {deletingId === p.id ? 'Removing…' : 'Delete participant'}
                        </button>
                      </div>
                    )}
                    <Section title="Personal">
                      <InfoRow label="Gender"        value={p.gender} />
                      <InfoRow label="Date of Birth" value={fmtDate(p.dateOfBirth)} />
                      <InfoRow label="Phone"         value={p.contactPhone} />
                      <InfoRow label="Email"         value={p.contactEmail} />
                    </Section>

                    {(p.guardianName || p.guardianPhone || p.guardianEmail) && (
                      <Section title="Guardian / Parent">
                        <InfoRow label="Name"  value={p.guardianName} />
                        <InfoRow label="Phone" value={p.guardianPhone} />
                        <InfoRow label="Email" value={p.guardianEmail} />
                        <InfoRow label="Passport No." value={p.guardianPassportNumber} />
                        <InfoRow label="Nationality"  value={p.guardianNationality} />
                      </Section>
                    )}

                    {(p.emergencyContactName || p.emergencyContactPhone) && (
                      <Section title="Emergency Contact">
                        <InfoRow label="Name"  value={p.emergencyContactName} />
                        <InfoRow label="Phone" value={p.emergencyContactPhone} />
                      </Section>
                    )}

                    <Section title="Medical / Dietary">
                      <InfoRow label="Allergies"          value={p.allergies} />
                      <InfoRow label="Dietary"            value={p.dietaryRequirements} />
                      <InfoRow label="Medical Conditions" value={p.conditions} />
                      <InfoRow label="Safety Info"        value={p.safetyInfo} />
                      <InfoRow label="Swimming Level"     value={p.swimmingLevel} />
                    </Section>

                    <Section title="Passport / Travel">
                      <InfoRow label="Passport No."     value={p.passportNumber} />
                      <InfoRow label="Expiry"           value={fmtDate(p.passportExpiry)} />
                      <InfoRow label="Nationality"      value={p.nationality} />
                      <InfoRow label="Country of Issue" value={p.countryOfIssue} />
                    </Section>

                    {p.formVariant === 'mic_cup' && (
                      <>
                        <Section title="Playing Details">
                          <InfoRow label="Shirt Number" value={p.shirtNumber} />
                          <InfoRow label="Position"     value={p.position} />
                        </Section>

                        <Section title="Travel Arrangements">
                          <InfoRow label="Flight Arrangement"     value={p.flightArrangementMethod} />
                          <InfoRow label="Travel Group Size"      value={p.travelGroupSize} />
                          <InfoRow label="Outbound Flight Details" value={p.outboundFlightDetails} />
                          <InfoRow label="Outbound Flight Number"  value={p.outboundFlightNumber} />
                          <InfoRow label="Return Flight Details"   value={p.returnFlightDetails} />
                          <InfoRow label="Return Flight Number"    value={p.returnFlightNumber} />
                        </Section>

                        <Section title="Insurance">
                          <InfoRow label="Declaration Confirmed" value={p.insuranceConfirmed ? 'Yes' : 'No'} />
                        </Section>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}
